/**
 * Centralized environment variable access with validation.
 * Use this instead of accessing import.meta.env directly throughout the codebase.
 *
 * @example
 * import { ENV, isDev, isProd } from '@/config/environment';
 *
 * const apiUrl = ENV.API_BASE_URL;
 * if (isDev()) { console.log('Development mode'); }
 */

// ============================================================================
// ENVIRONMENT VARIABLES
// ============================================================================

/**
 * All environment variables used in the application.
 * Values are read from import.meta.env with fallback defaults.
 */
export const ENV = {
  // ============================================
  // API Configuration
  // ============================================

  /** Base URL for API requests */
  API_BASE_URL: (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:4000/api',

  /** WebSocket server URL */
  WS_URL: (import.meta.env.VITE_WS_URL as string) || 'ws://localhost:4000',

  /** API request timeout in milliseconds */
  API_TIMEOUT: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,

  // ============================================
  // Authentication
  // ============================================

  /** Client secret for encryption */
  CLIENT_SECRET: (import.meta.env.VITE_CLIENT_SECRET as string) || 'dev-secret-key',

  /** OAuth client ID (if using OAuth) */
  OAUTH_CLIENT_ID: import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined,

  /** OAuth redirect URI */
  OAUTH_REDIRECT_URI: import.meta.env.VITE_OAUTH_REDIRECT_URI as string | undefined,

  // ============================================
  // External Services
  // ============================================

  /** Sentry DSN for error tracking */
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN as string | undefined,

  /** Google Analytics ID */
  GA_ID: import.meta.env.VITE_GA_ID as string | undefined,

  /** Push notification VAPID public key */
  VAPID_PUBLIC_KEY: import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined,

  // ============================================
  // Application Settings
  // ============================================

  /** Application name */
  APP_NAME: (import.meta.env.VITE_APP_NAME as string) || 'CCMS',

  /** Application version */
  APP_VERSION: (import.meta.env.VITE_APP_VERSION as string) || '1.0.0',

  /** Default locale */
  DEFAULT_LOCALE: (import.meta.env.VITE_DEFAULT_LOCALE as string) || 'en',

  // ============================================
  // Environment Flags
  // ============================================

  /** Current environment mode */
  MODE: import.meta.env.MODE as 'development' | 'production' | 'test',

  /** Is development environment */
  IS_DEV: import.meta.env.DEV as boolean,

  /** Is production environment */
  IS_PROD: import.meta.env.PROD as boolean,

  /** Is server-side rendering */
  IS_SSR: import.meta.env.SSR as boolean,

  /** Base URL for the application */
  BASE_URL: import.meta.env.BASE_URL as string,
} as const;

// ============================================================================
// ENVIRONMENT HELPERS
// ============================================================================

/**
 * Check if running in development mode
 * @returns True if in development mode
 */
export function isDev(): boolean {
  return ENV.IS_DEV;
}

/**
 * Check if running in production mode
 * @returns True if in production mode
 */
export function isProd(): boolean {
  return ENV.IS_PROD;
}

/**
 * Check if running in test mode
 * @returns True if in test mode
 */
export function isTest(): boolean {
  return ENV.MODE === 'test';
}

/**
 * Check if running on server (SSR)
 * @returns True if server-side rendering
 */
export function isServer(): boolean {
  return ENV.IS_SSR;
}

/**
 * Check if running in browser
 * @returns True if in browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Required environment variables that must be set in production
 */
const REQUIRED_IN_PROD: (keyof typeof ENV)[] = ['API_BASE_URL', 'CLIENT_SECRET'];

/**
 * Validate that all required environment variables are set.
 * Logs warnings in development, throws in production.
 */
export function validateEnvironment(): void {
  const missing: string[] = [];

  for (const key of REQUIRED_IN_PROD) {
    const value = ENV[key];
    if (value === undefined || value === '' || value === null) {
      missing.push(key);
    }
  }

  if (missing.length > 0) {
    const message = `Missing required environment variables: ${missing.join(', ')}`;

    if (isProd()) {
      throw new Error(message);
    } else {
      console.warn(`[ENV] ${message}`);
    }
  }
}

/**
 * Get environment variable with fallback
 * @param key - Environment variable key
 * @param fallback - Fallback value if not set
 * @returns The environment variable value or fallback
 */
export function getEnv<K extends keyof typeof ENV>(key: K, fallback?: (typeof ENV)[K]): (typeof ENV)[K] {
  const value = ENV[key];
  if (value === undefined || value === '' || value === null) {
    if (fallback !== undefined) {
      return fallback;
    }
  }
  return value;
}

/**
 * Check if an environment variable is set
 * @param key - Environment variable key
 * @returns True if the variable is set and not empty
 */
export function hasEnv(key: keyof typeof ENV): boolean {
  const value = ENV[key];
  return value !== undefined && value !== '' && value !== null;
}

// ============================================================================
// LOGGING CONFIGURATION
// ============================================================================

/**
 * Log levels based on environment
 */
export const LOG_LEVEL = {
  /** Log everything */
  DEBUG: isDev(),
  /** Log info and above */
  INFO: true,
  /** Log warnings and above */
  WARN: true,
  /** Log errors only */
  ERROR: true,
} as const;

/**
 * Conditional console log that respects log levels
 * @param level - Log level
 * @param message - Message to log
 * @param args - Additional arguments
 */
export function log(level: keyof typeof LOG_LEVEL, message: string, ...args: unknown[]): void {
  if (!LOG_LEVEL[level]) return;

  const prefix = `[${level}]`;

  switch (level) {
    case 'DEBUG':
      console.debug(prefix, message, ...args);
      break;
    case 'INFO':
      console.info(prefix, message, ...args);
      break;
    case 'WARN':
      console.warn(prefix, message, ...args);
      break;
    case 'ERROR':
      console.error(prefix, message, ...args);
      break;
  }
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type Environment = typeof ENV;
export type EnvironmentKey = keyof typeof ENV;
